"use client";

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchAutomationsEntitlement } from "../../client/manager/automations";
import {
  isSubscribedStatus,
  type AutomationsEntitlementSnapshot,
  type AutomationsEntitlementStatus,
} from "../../models/automations-entitlement";
import { automationsKeys } from "./automationsQueryKeys";

const POLL_INTERVAL_MS = 1000;
const POLL_CAP_MS = 30_000;

export interface UseAutomationsEntitlementResult {
  /** Fail-closed `free` until the read resolves (and on error) — gate
   *  state-dependent chrome on `hydrated` and `isError`, never on this value
   *  alone, or an entitled comedian sees a flash of the free preview. */
  status: AutomationsEntitlementStatus;
  /** True once the read has settled, successfully or not. */
  hydrated: boolean;
  /** The read failed: show an error, not a false Not purchased. */
  isError: boolean;
  /** automationsState is Subscribed; false while loading and on error. */
  subscribed: boolean;
  snapshot: AutomationsEntitlementSnapshot | null;
  /** `pollUntilSubscribed` stopped without reaching Subscribed: its 30s ran out or the read failed. */
  pollGaveUp: boolean;
}

/**
 * Live Stripe entitlement for one comedian. Pass `enabled: false` where the
 * `automations_tab` gate is off — the route 404s for ungated comedians.
 * `pollUntilSubscribed` refetches every second until the plan reads Subscribed,
 * giving up after 30s (right after a checkout payment, before Stripe settles).
 */
export function useAutomationsEntitlement(
  comedianId: string,
  opts?: { enabled?: boolean; pollUntilSubscribed?: boolean }
): UseAutomationsEntitlementResult {
  const polling = opts?.pollUntilSubscribed ?? false;
  const [pollExpired, setPollExpired] = useState(false);
  useEffect(() => {
    if (!polling) return;
    setPollExpired(false);
    const timer = setTimeout(() => setPollExpired(true), POLL_CAP_MS);
    return () => clearTimeout(timer);
  }, [polling]);

  const query = useQuery({
    queryKey: automationsKeys.entitlement(comedianId),
    queryFn: () => fetchAutomationsEntitlement(comedianId),
    enabled: !!comedianId && (opts?.enabled ?? true),
    staleTime: 2 * 60 * 1000,
    retry: false,
    refetchInterval: (q) =>
      polling &&
      !pollExpired &&
      !(q.state.data && isSubscribedStatus(q.state.data.status))
        ? POLL_INTERVAL_MS
        : false,
  });

  if (query.isError) {
    return {
      status: "free",
      hydrated: true,
      isError: true,
      subscribed: false,
      snapshot: null,
      pollGaveUp: polling,
    };
  }
  const status = query.data?.status ?? "free";
  const subscribed = isSubscribedStatus(status);
  return {
    status,
    hydrated: query.isSuccess,
    isError: false,
    subscribed,
    snapshot: query.data?.snapshot ?? null,
    pollGaveUp: polling && pollExpired && !subscribed,
  };
}
